import {OptionItem} from '../ui/option-card/option-card.model';

// Shared file-format vocabulary for the export feature (saved exports + quick export).
// Mirrors the openapi.yaml `savedExportOutputFormat` enum (resolved from ioformats.outputformat,
// constrained by CHK_IOF_OUTPUTFORMAT). Hand-mirrored like savedExportRecurrences — no codegen.
export const savedExportOutputFormats = ['CSV', 'EXCEL', 'JSON'] as const;
export type SavedExportOutputFormat = (typeof savedExportOutputFormats)[number];

function isSavedExportOutputFormat(value: string): value is SavedExportOutputFormat {
    return savedExportOutputFormats.some(format => format === value);
}

const outputFormatLabels: Record<SavedExportOutputFormat, string> = {
    CSV: 'CSV',
    EXCEL: 'XLSX',
    JSON: 'JSON',
};

const outputFormatBadgeClasses: Record<SavedExportOutputFormat, string> = {
    CSV: 'text-primary',
    EXCEL: 'text-success',
    JSON: 'format-json',
};

// MIME type + file extension per format, for the download Blob and filename.
type OutputFormatFileMeta = {mimeType: string; extension: string};

export const outputFormatFileMeta: Record<SavedExportOutputFormat, OutputFormatFileMeta> = {
    CSV: {mimeType: 'text/csv', extension: 'csv'},
    EXCEL: {
        mimeType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        extension: 'xlsx',
    },
    JSON: {mimeType: 'application/json', extension: 'json'},
};

// null => no pill. Unrecognised value => show it raw rather than break, so a future enum addition
// degrades gracefully until the mirror is updated.
export function outputFormatLabel(outputFormat: string | null): string | null {
    if (outputFormat === null) {
        return null;
    }
    return isSavedExportOutputFormat(outputFormat)
        ? outputFormatLabels[outputFormat]
        : outputFormat;
}

export function outputFormatBadgeClass(outputFormat: string | null): string {
    return outputFormat !== null && isSavedExportOutputFormat(outputFormat)
        ? outputFormatBadgeClasses[outputFormat]
        : 'text-secondary';
}

// null for null/unknown formats; a caller naming a download omits the extension then.
export function outputFormatExtension(outputFormat: string | null): string | null {
    return outputFormat !== null && isSavedExportOutputFormat(outputFormat)
        ? outputFormatFileMeta[outputFormat].extension
        : null;
}

export type QuickExportMode = 'specific-date' | 'schedule';

export type ExportFormat = OptionItem & {
    outputFormat: SavedExportOutputFormat;
    modes: ReadonlyArray<QuickExportMode>;
    defaultForModes: ReadonlyArray<QuickExportMode>;
};

// Exported on its own: the customized wizard's file-format step shows this entry as its single,
// pre-selected format.
export const csvExportFormat: ExportFormat = {
    id: 'csv',
    outputFormat: 'CSV',
    titleKey: 'quickexports_export_format_csv_name',
    descriptionKey: 'quickexports_export_format_csv_desc',
    modes: ['specific-date', 'schedule'],
    defaultForModes: ['schedule'],
};

export const exportFormats: ReadonlyArray<ExportFormat> = [
    csvExportFormat,
    {
        id: 'xlsx',
        outputFormat: 'EXCEL',
        titleKey: 'quickexports_export_format_xlsx_name',
        descriptionKey: 'quickexports_export_format_xlsx_desc',
        modes: ['specific-date', 'schedule'],
        defaultForModes: ['specific-date'],
    },
    {
        id: 'json',
        outputFormat: 'JSON',
        titleKey: 'quickexports_export_format_json_name',
        descriptionKey: 'quickexports_export_format_json_desc',
        modes: ['specific-date', 'schedule'],
        defaultForModes: [],
    },
];

export function formatsForMode(mode: QuickExportMode): ReadonlyArray<ExportFormat> {
    return exportFormats.filter(format => format.modes.includes(mode));
}

export function defaultFormatForMode(mode: QuickExportMode): ExportFormat {
    const format = exportFormats.find(candidate => candidate.defaultForModes.includes(mode));
    if (format === undefined) {
        throw new Error(`No default export format for mode '${mode}'`);
    }
    return format;
}
